import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { X, GripVertical, Eye, EyeOff, BarChart3, Map, TrendingUp, Grid3x3 } from 'lucide-react';
export interface DashboardCard {
  id: string;
  name: string;
  icon: any;
  visible: boolean;
  description: string;
  size: 'small' | 'large';
}
interface CardCustomizationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  cards: DashboardCard[];
  onCardsChange: (cards: DashboardCard[]) => void;
}
export function CardCustomizationPanel({
  isOpen,
  onClose,
  cards,
  onCardsChange
}: CardCustomizationPanelProps) {
  const toggleCardVisibility = (id: string) => {
    onCardsChange(cards.map(card => card.id === id ? {
      ...card,
      visible: !card.visible
    } : card));
  };
  const resetLayout = () => {
    onCardsChange(cards.map(card => ({
      ...card,
      visible: true
    })));
  };
  const visibleCount = cards.filter(c => c.visible).length;
  return <AnimatePresence data-id="element-18">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40" data-id="element-19" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        x: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} className="fixed top-20 right-6 w-96 bg-white/95 backdrop-blur-xl border border-slate-100 rounded-xl shadow-2xl z-50 overflow-hidden" data-id="element-20">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100" data-id="element-21">
              <div data-id="element-22">
                <h3 className="text-lg font-semibold text-slate-900" data-id="element-23">
                  Dashboard Cards
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-24">
                  {visibleCount} of {cards.length} cards visible
                </p>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-900" data-id="element-25">
                <X size={18} data-id="element-26" />
              </button>
            </div>

            {/* Cards List */}
            <div className="p-4 max-h-[500px] overflow-y-auto custom-scrollbar" data-id="element-27">
              <Reorder.Group axis="y" values={cards} onReorder={onCardsChange} className="space-y-2" data-id="element-28">
                {cards.map(card => <Reorder.Item key={card.id} value={card} className="bg-white border border-slate-200 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow" data-id="element-29">
                    <div className="flex items-start gap-3" data-id="element-30">
                      {/* Drag Handle */}
                      <div className="mt-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing" data-id="element-31">
                        <GripVertical size={16} data-id="element-32" />
                      </div>

                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${card.visible ? 'bg-gradient-to-br from-pink-500 to-purple-500 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-33">
                        <card.icon size={16} data-id="element-34" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0" data-id="element-35">
                        <div className="flex items-center gap-2 mb-1" data-id="element-36">
                          <h4 className="text-sm font-semibold text-slate-900 truncate" data-id="element-37">
                            {card.name}
                          </h4>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${card.size === 'large' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-38">
                            {card.size}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed" data-id="element-39">
                          {card.description}
                        </p>
                      </div>

                      {/* Visibility Toggle */}
                      <button onClick={() => toggleCardVisibility(card.id)} className={`p-1.5 rounded-lg transition-all ${card.visible ? 'bg-pink-50 text-pink-600 hover:bg-pink-100' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`} data-id="element-40">
                        {card.visible ? <Eye size={16} data-id="element-41" /> : <EyeOff size={16} data-id="element-42" />}
                      </button>
                    </div>
                  </Reorder.Item>)}
              </Reorder.Group>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50" data-id="element-43">
              <div className="flex items-center justify-between text-xs" data-id="element-44">
                <span className="text-slate-500" data-id="element-45">
                  Drag to reorder • Click eye to show/hide
                </span>
                <button onClick={resetLayout} className="text-pink-600 hover:text-pink-700 font-medium" data-id="element-46">
                  Reset Layout
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}